/**
 * Spec 106 — PersonaFindingCard.
 *
 * Decomposes an OBSERVATIONAL response persona-fit finding into priority /
 * [wellbeing] / trust / anti-signal rows. Shows observed_visibility with sample
 * size + CI, an observational lens label, and a persona-confidence badge. Each
 * dimension expands to its verbatim response quote + reasoning. Informational
 * personas render priority and wellbeing as TWO DISTINCT rows — never summed.
 *
 * This card carries ONLY the persona-definition caveat (the confidence badge) —
 * NOT the page-encounter caveat (that is spec 107's).
 *
 * data-testids (per spec input): persona-finding-card, finding-observational-label,
 * finding-confidence-badge, finding-visibility-ci, finding-dim-priority,
 * finding-dim-wellbeing, finding-dim-trust, finding-dim-anti-signal,
 * finding-dim-quote.
 */
import { useState } from 'react';
import { Icon } from './ui/Icon.js';
import { PersonaConfidenceBadge } from './PersonaConfidenceBadge.js';
import type {
  PersonaDimensionResult,
  PersonaResponseFinding,
  ScoredDimensionType,
} from '../api/personaEvaluation.js';

const DIM_LABEL: Record<ScoredDimensionType, string> = {
  priority: 'Priority-fit',
  wellbeing: 'Wellbeing-fit',
  trust: 'Trust (cited sources)',
  anti_signal: 'Anti-signal penalty',
};

const DIM_TESTID: Record<ScoredDimensionType, string> = {
  priority: 'finding-dim-priority',
  wellbeing: 'finding-dim-wellbeing',
  trust: 'finding-dim-trust',
  anti_signal: 'finding-dim-anti-signal',
};

function fmt(n: number | null): string {
  return n == null ? '—' : n.toFixed(2);
}

function DimensionRow({
  type,
  score,
  results,
}: {
  type: ScoredDimensionType;
  score: number | null;
  results: PersonaDimensionResult[];
}) {
  const [open, setOpen] = useState(false);
  const ofType = results.filter((r) => r.dimension_type === type);
  const hasDetail = ofType.length > 0;
  return (
    <div data-testid={DIM_TESTID[type]} className="finding-dim-row">
      <button
        type="button"
        data-testid="dimension-row-toggle"
        aria-expanded={open}
        className="finding-dim-header group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-1"
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="bx-chevron-right" className={`shrink-0 text-slate-400 transition-transform duration-150 group-hover:text-slate-500 ${
            open ? 'rotate-90' : ''
          }`} />
        <span className="flex-1">{DIM_LABEL[type]}</span>
        <span className="finding-dim-score tabular-nums text-slate-900">{fmt(score)}</span>
      </button>
      {open && hasDetail && (
        <div className="finding-dim-detail space-y-3 pl-7 pr-2 pt-2 pb-1">
          {ofType.map((r) => (
            <div key={r.id} className="space-y-1.5">
              {r.supporting_quote && (
                <figure className="m-0">
                  <figcaption className="mb-1 text-xs font-semibold text-slate-400">
                    What the AI said
                  </figcaption>
                  <blockquote
                    data-testid="finding-dim-quote"
                    className="m-0 rounded-r-md border-l-[3px] border-amber-400 bg-amber-50/50 py-2 pl-3 pr-3 text-sm leading-relaxed text-slate-700"
                  >
                    “{r.supporting_quote}”
                  </blockquote>
                </figure>
              )}
              {r.reasoning && (
                <p className="m-0 text-sm leading-relaxed text-slate-600">
                  <span className="font-medium text-slate-500">Why it scored: </span>
                  {r.reasoning}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function PersonaFindingCard({
  finding,
}: {
  finding: PersonaResponseFinding;
}) {
  const results = finding.dimension_results ?? [];
  const isInformational = finding.persona_type === 'informational';
  const evidence = finding.response_evidence_strength;
  const ci = evidence?.ci ?? null;

  return (
    // Spec 235 — this card's OWN chrome (rounded-lg/border/bg-white/shadow-sm)
    // used to duplicate a card inside the disclosure Row that PersonaEvaluationsPage
    // already wraps it in (`.hub-row`, which supplies the same padding via a
    // token). Dropped here rather than earlier, since that page's own task was
    // the row-family proving ground, not this file's restyle, and this file
    // carries its own gate (144): dimension-row-toggle, aria-expanded,
    // leading-relaxed and finding-observational-label all stay, and the quote
    // styling below stays upright, none of that living in the chrome removed.
    <div
      data-testid="persona-finding-card"
      className="persona-finding-card"
    >
      <div className="flex flex-wrap items-center gap-2 mb-2">
        {/* Observational label — NEVER the page-encounter caveat. */}
        <span
          data-testid="finding-observational-label"
          className="finding-observational-label inline-flex items-center rounded-full bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700 border border-violet-200"
          title="What the AI engine actually did (observational); never blended with the GEO score"
        >
          Observed · response persona-fit
        </span>
        {/* Persona-definition caveat (inherited confidence). */}
        <span data-testid="finding-confidence-badge">
          <PersonaConfidenceBadge confidence={finding.persona_confidence_at_eval} />
        </span>
        <span className="text-xs text-slate-500">
          persona v{finding.persona_version}
        </span>
        <span className="text-xs text-slate-500">
          status: {finding.status}
        </span>
      </div>

      {finding.status === 'insufficient_data' ? (
        <p data-testid="finding-visibility-ci" className="text-sm font-semibold text-amber-700">
          Insufficient data — couldn’t measure (sample n=
          {evidence?.sample_size ?? 0}, floor not met). Visibility is not 0; it is
          unknown.
        </p>
      ) : (
        <p data-testid="finding-visibility-ci" className="text-sm text-slate-700 mb-2.5">
          Observed visibility:{' '}
          <strong>
            {finding.observed_visibility == null
              ? '—'
              : `${Math.round(finding.observed_visibility * 100)}%`}
          </strong>{' '}
          {evidence && (
            <span className="text-slate-500">
              (n={evidence.sample_size}
              {ci ? `, 95% CI ±${ci.half_width.toFixed(2)}` : ''})
            </span>
          )}
        </p>
      )}

      {finding.status === 'completed' && (
        <div className="finding-dimensions space-y-3">
          <DimensionRow type="priority" score={finding.priority_fit} results={results} />
          {isInformational && (
            <DimensionRow
              type="wellbeing"
              score={finding.wellbeing_fit}
              results={results}
            />
          )}
          <DimensionRow type="trust" score={finding.trust} results={results} />
          <DimensionRow
            type="anti_signal"
            score={finding.anti_signal_penalty}
            results={results}
          />
        </div>
      )}
    </div>
  );
}
